<script lang="ts" setup>
import { ref } from 'vue';
import useLocale from './helpers/use-locale';

type Title = {
  text: string;
  params?: string[];
};

type DialogOptions = {
  width: number;
  zIndex: number;
  noConfirm: boolean;
};

type Props = {
  roundedDialog?: 'sm' | 'default' | 'lg';
  roundedButton?: 'sm' | 'default' | 'lg';
};

withDefaults(defineProps<Props>(), { roundedDialog: 'lg', roundedButton: 'default' });

const { t } = useLocale();

const dialog = ref<boolean>(false);
const resolve = ref<unknown | null>(null);
const reject = ref<unknown | null>(null);
const titleTranslationKey = ref<string | null>(null);
const titleTranslationParams = ref<string[] | undefined>(undefined);
const messageTranslationKey = ref<string | null>(null);
const cancelTranslationKey = ref<string | null>(null);
const confirmTranslationKey = ref<string | null>(null);
const dialogOptions = ref<DialogOptions>({
  width: 498,
  zIndex: 3000,
  noConfirm: false,
});

const open = (
  title: Title | string,
  messageKey: string,
  cancelKey: string,
  confirmKey: string,
  options?: DialogOptions,
) => {
  dialog.value = true;
  titleTranslationKey.value = typeof title === 'string' ? title : title.text;
  titleTranslationParams.value = typeof title === 'string' ? undefined : title.params;
  messageTranslationKey.value = messageKey;
  cancelTranslationKey.value = cancelKey;
  confirmTranslationKey.value = confirmKey;
  dialogOptions.value = Object.assign(dialogOptions.value, options);

  return new Promise((responseResolve, responseReject) => {
    resolve.value = responseResolve;
    reject.value = responseReject;
  });
};

const confirm = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  resolve.value(true);
  dialog.value = false;
};

const cancel = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  resolve.value(false);
  dialog.value = false;
};

defineExpose({ open });
</script>

<template>
  <v-dialog
    v-model="dialog"
    :max-width="dialogOptions.width"
    :style="{ zIndex: dialogOptions.zIndex }"
    retain-focus
    @keydown.esc="cancel"
  >
    <v-card class="secondaryBackground" :class="roundedDialog === 'default' ? 'rounded' : `rounded-${roundedDialog}`">
      <v-card-title
        v-if="
          titleTranslationParams !== undefined &&
          titleTranslationParams !== null &&
          titleTranslationKey !== undefined &&
          titleTranslationKey !== null
        "
        class="confirmation-title text-center text--black justify-center px-16 pt-10 pb-5"
      >
        {{ t(titleTranslationKey) }}
        <span v-if="titleTranslationParams[0] !== null">{{ titleTranslationParams[0] }}</span
        >&nbsp;
        <span v-if="titleTranslationParams[1] !== null">{{ titleTranslationParams[1] }}?</span>
      </v-card-title>
      <v-card-title v-else class="confirmation-title text-center text--black justify-center px-16 pt-10 pb-5">
        <span v-if="titleTranslationKey !== undefined && titleTranslationKey !== null">{{
          t(titleTranslationKey)
        }}</span>
      </v-card-title>

      <v-card-text v-if="messageTranslationKey !== null" class="text-on-surface text-center">
        {{ t(messageTranslationKey) }}
      </v-card-text>

      <v-card-actions class="d-flex justify-space-between pb-6">
        <v-spacer />
        <v-btn
          v-if="!dialogOptions.noConfirm && cancelTranslationKey !== null"
          color="secondary"
          :rounded="roundedButton === 'default' ? 'true' : roundedButton"
          min-width="130"
          @click="cancel"
        >
          <span class="text-none text-on-secondary">{{ t(cancelTranslationKey) }}</span>
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :rounded="roundedButton === 'default' ? 'true' : roundedButton"
          min-width="130"
          @click="confirm"
        >
          <span v-if="confirmTranslationKey !== null" class="text-none text-on-primary">{{
            t(confirmTranslationKey)
          }}</span>
        </v-btn>
        <v-spacer />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.confirmation-title {
  font:
    bold 26px/1.5 Bitter,
    sans-serif;
  white-space: pre-line;
}
</style>
